import { apiGatewayClient } from '@/common/apis/client';
import { useDiscountInquiry } from '@/common/apis/services/factor/discountInquiry';
import { ClinicStatus } from '@/common/constants/status/clinicStatus';
import { useState } from 'react';
import { mapDiscountInquirySuccess, unwrapDiscountInquiryPayload } from './mapDiscountInquiry';

export const useDiscount = ({
  bookId,
  centerId,
  serviceId,
  userCenterId,
}: {
  bookId: string;
  centerId?: string;
  serviceId?: string;
  userCenterId?: string;
}) => {
  const discountInquiry = useDiscountInquiry();
  const [invoiceDetails, setInvoiceDetails] = useState<{
    totalPrice?: string;
    discount?: string;
    discountToken?: string;
    tax?: string;
    isValidDiscount?: boolean;
    discountErrorMessage?: string;
    code?: string;
  }>({});
  const [gatewayInquiryLoading, setGatewayInquiryLoading] = useState(false);

  const applyFailedDiscount = (message?: string) => {
    setInvoiceDetails(prev => ({
      ...prev,
      isValidDiscount: false,
      discountErrorMessage: message || 'کد تخفیف معتبر نیست.',
    }));
  };

  const applySuccessfulDiscount = (payload: unknown, code: string) => {
    const unwrapped = unwrapDiscountInquiryPayload(payload);
    setInvoiceDetails(prev => ({
      ...prev,
      ...mapDiscountInquirySuccess(unwrapped.result, code),
      discountErrorMessage: undefined,
    }));
  };

  const handleDiscountSubmit = async (code: string) => {
    try {
      if (!bookId && serviceId && centerId && userCenterId) {
        setGatewayInquiryLoading(true);
        const { data } = await apiGatewayClient.get('https://apigw.paziresh24.com/v1/discount/inquiry', {
          params: {
            user_center_id: userCenterId,
            center_id: centerId,
            code: code,
            service_id: serviceId,
          },
        });

        const payload = unwrapDiscountInquiryPayload(data);
        if (payload?.status === ClinicStatus.SUCCESS) {
          applySuccessfulDiscount(payload, code);
          return;
        }
        applyFailedDiscount(payload?.message);
        return;
      }
      const { data } = await discountInquiry.mutateAsync({
        book_id: bookId,
        centerId,
        serviceId,
        userCenterId,
        code,
      });
      const payload = unwrapDiscountInquiryPayload(data);
      if (payload?.status === ClinicStatus.SUCCESS) {
        applySuccessfulDiscount(payload, code);
        return;
      }
      applyFailedDiscount(payload?.message);
    } catch (error: unknown) {
      const axiosMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      applyFailedDiscount(axiosMessage || 'خطا در بررسی کد تخفیف.');
    } finally {
      setGatewayInquiryLoading(false);
    }
  };

  return { ...invoiceDetails, handleDiscountSubmit, discountLoading: gatewayInquiryLoading || discountInquiry.isLoading };
};

export default useDiscount;
